/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import typography from '@amblique/base/app/theme/foundations/typography';

/* eslint-disable sort-keys */
export default {
  ...typography,

  lineHeights: {
    ...typography.lineHeights,

    none: 1,
    shorter: 1.2,
    short: 1.3,
    base: 1.4,
    tall: 1.6,
    taller: 2,
  },

  letterSpacings: {
    ...typography.letterSpacings,

    slightlyTight: '-0.0275rem', // 0.44px
  },

  fontSizes: {
    ...typography.fontSizes,

    /**
     * @override default Chakra font sizes to King Living font sizes
     */
    '3xs': '0.45rem', // 7.2px
    '2xs': '0.625rem', // 10px
    'xs': '0.75rem', // 12px
    'sm': '0.875rem', // 14px
    'md': '1rem', // 16px
    'lg': '1.125rem', // 18px
    'xl': '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem', // 48px
    '6xl': '3.75rem', // 60px
    '7xl': '4.5rem', // 72px
    '8xl': '6rem', // 96px
    '9xl': '8rem', // 128px

    /**
     * @deprecated Use explicit font sizes instead
     */
    '2.75xs': '0.6875rem', // 11px
    '1xs': '0.8125rem', // 13px
    '1.5xs': '0.8125rem', // 13px
    '2sm': '0.9375rem', // 15px
    '1.5xl': '1.375rem', // 22px
    '2.5xl': '1.625rem', // 26px
    '2.75xl': '1.75rem', // 28px
    '3.25xl': '2.0125rem', // 32px
    '3.5xl': '2.125rem', // 34px
    '4.5xl': '2.375rem', // 38px
    '4.75xl': '2.5rem', // 40px
  },

  fonts: {
    heading: 'aktiv-grotesk, "sans-serif"',
    body: 'aktiv-grotesk, "sans-serif"',
    mono: 'aktiv-grotesk-thin, "sans-serif"',
  },

  // Typography preset system (textStyles)
  // Use via textStyle prop: <Text textStyle="displayXL">...</Text>
  // Responsive font sizes: base (mobile) and lg (desktop)
  // Chakra UI breakpoints: base: 0em, lg: 62em
  textStyles: {
    // Display XL: Mobile 64px → Desktop 96px
    displayXL: {
      fontSize: {base: '4rem', lg: '6rem'}, // 64px base → 96px lg
      fontWeight: 400,
      lineHeight: 1, // 100%
      fontFamily: 'heading',
    },
    // Display L: Mobile 48px → Desktop 72px
    displayL: {
      fontSize: {base: '3rem', lg: '4.5rem'}, // 48px base → 72px lg
      fontWeight: 400,
      lineHeight: 1, // 100%
      fontFamily: 'heading',
    },
    // Display M: Mobile 40px → Desktop 60px
    displayM: {
      fontSize: {base: '2.5rem', lg: '3.75rem'}, // 40px base → 60px lg
      fontWeight: 500,
      lineHeight: 1.2, // 120%
      fontFamily: 'heading',
    },
    // Heading XL: Mobile 32px → Desktop 40px
    headingXL: {
      fontSize: {base: '2rem', lg: '2.5rem'}, // 32px base → 40px lg
      fontWeight: 500,
      lineHeight: 1.2, // 120%
      fontFamily: 'heading',
    },
    // Heading L: Mobile 24px → Desktop 32px
    headingL: {
      fontSize: {base: '1.5rem', lg: '2rem'}, // 24px base → 32px lg
      fontWeight: 500,
      lineHeight: 1.4, // 140%
      fontFamily: 'heading',
    },
    // Label XL: Mobile 20px → Desktop 24px
    labelXL: {
      fontSize: {base: '1.25rem', lg: '1.5rem'}, // 20px base → 24px lg
      fontWeight: 500,
      lineHeight: 1.4, // 140%
      fontFamily: 'body',
    },
    // Body XL: Mobile 20px → Desktop 24px
    bodyXL: {
      fontSize: {base: '1.25rem', lg: '1.5rem'}, // 20px base → 24px lg
      fontWeight: 400,
      lineHeight: 1.4, // 140%
      fontFamily: 'body',
    },
    // Label L: Mobile 16px → Desktop 18px
    labelL: {
      fontSize: {base: '1rem', lg: '1.125rem'}, // 16px base → 18px lg
      fontWeight: 500,
      lineHeight: 1.6, // 160%
      fontFamily: 'body',
    },
    // Body L: Mobile 16px → Desktop 18px
    bodyL: {
      fontSize: {base: '1rem', lg: '1.125rem'}, // 16px base → 18px lg
      fontWeight: 400,
      lineHeight: 1.6, // 160%
      fontFamily: 'body',
    },
    // Label default: Mobile 14px → Desktop 16px
    labelDefault: {
      fontSize: {base: '0.875rem', lg: '1rem'}, // 14px base → 16px lg
      fontWeight: 600,
      lineHeight: 1.4, // 140%
      fontFamily: 'body',
    },
    // Body default: Mobile 14px → Desktop 16px
    bodyDefault: {
      fontSize: {base: '0.875rem', lg: '1rem'}, // 14px base → 16px lg
      fontWeight: 400,
      lineHeight: 1.4, // 140%
      fontFamily: 'body',
    },
    // Label S: Mobile 13px → Desktop 12px
    labelS: {
      fontSize: {base: '0.8125rem', lg: '0.75rem'}, // 13px base → 12px lg
      fontWeight: 500,
      lineHeight: 1.2, // 120%
      fontFamily: 'body',
    },
    // Body S: Mobile 13px → Desktop 12px
    bodyS: {
      fontSize: {base: '0.8125rem', lg: '0.75rem'}, // 13px base → 12px lg
      fontWeight: 400,
      lineHeight: 1.2, // 120%
      fontFamily: 'body',
    },
    // Eyebrow Semi 12: Mobile 12px → Desktop 12px
    eyebrowSemi12: {
      fontSize: {base: '0.75rem', lg: '0.75rem'}, // 12px base → 12px lg
      fontWeight: 600,
      lineHeight: 1.2, // 120%
      fontFamily: 'body',
    },
    // Eyebrow Semi 10: Mobile 10px → Desktop 10px
    eyebrowSemi10: {
      fontSize: {base: '0.625rem', lg: '0.625rem'}, // 10px base → 10px lg
      fontWeight: 600,
      lineHeight: 1.2, // 120%
      fontFamily: 'body',
    },
  },
};
